import ArticleUpdaterService from "@components/Article/ArticleUpdater/ArticleUpdaterService";
import Tooltip from "@components/Atoms/Tooltip";
import isSameEmail from "@core/utils/isSameEmail";
import FetchService from "@core-ui/ApiServices/FetchService";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import PageDataContextService from "@core-ui/ContextServices/PageDataContext";
import BranchUpdaterService from "@ext/git/actions/Branch/BranchUpdaterService/logic/BranchUpdaterService";
import type { MergeRequestStatus } from "@ext/git/core/GitMergeRequest/components/Elements/Status";
import type { MergeRequest } from "@ext/git/core/GitMergeRequest/model/MergeRequest";
import t from "@ext/localization/locale/translate";
import { Button } from "@ui-kit/Button";
import { ConfirmPopover } from "@ui-kit/ConfirmPopover";
import { Loader } from "@ui-kit/Loader";
import { useState } from "react";

export type MergeButtonProps = {
	mergeRequest: MergeRequest;
	status: MergeRequestStatus;
	hasConflicts: boolean;
};

const useIsMergeAvailable = ({ mergeRequest, status, hasConflicts }: MergeButtonProps) => {
	const pageProps = PageDataContextService.value;

	if (status === "draft") return { disabled: true, reason: t("git.merge-requests.disable-button-reason.draft") };
	if (status === "in-progress")
		return { disabled: true, reason: t("git.merge-requests.disable-button-reason.not-approved") };
	if (status === "approved" && hasConflicts)
		return { disabled: true, reason: t("git.merge-requests.disable-button-reason.has-conflicts") };

	if (!isSameEmail(mergeRequest.creator.email, pageProps.user.info?.mail))
		return { disabled: true, reason: t("git.merge-requests.disable-button-reason.not-author") };

	return { disabled: false, reason: null };
};

const getBodyKey = (squash: boolean, deleteAfterMerge: boolean) => {
	if (squash) {
		return deleteAfterMerge
			? "git.merge-requests.confirm.body.squash-delete-branch-comment"
			: "git.merge-requests.confirm.body.squash-comment";
	}
	return deleteAfterMerge
		? "git.merge-requests.confirm.body.delete-branch-after-merge"
		: "git.merge-requests.confirm.body.not-delete-branch-after-merge";
};

const MergeConfirmDescription = ({ mergeRequest }: { mergeRequest: MergeRequest }) => {
	const bodyHtml = t(getBodyKey(mergeRequest.options?.squash, mergeRequest.options?.deleteAfterMerge))
		.replaceAll("{{sourceBranch}}", mergeRequest.sourceBranchRef)
		.replaceAll("{{targetBranch}}", mergeRequest.targetBranchRef);

	// biome-ignore lint/style/useNamingConvention: expected
	return <span className="article !bg-transparent" dangerouslySetInnerHTML={{ __html: bodyHtml }} />;
};

const useMerge = () => {
	const apiUrlCreator = ApiUrlCreatorService.value;
	const [isMerging, setIsMerging] = useState(false);

	const merge = async () => {
		setIsMerging(true);
		try {
			const res = await FetchService.fetch(apiUrlCreator.validateMerge());
			if (!res.ok) return;

			await FetchService.fetch(apiUrlCreator.mergeRequestMerge(false));
			await BranchUpdaterService.updateBranch(apiUrlCreator);
			await ArticleUpdaterService.update(apiUrlCreator);
		} finally {
			setIsMerging(false);
		}
	};

	return { merge, isMerging };
};

const MergeButton = ({ mergeRequest, status }: MergeButtonProps) => {
	const { disabled, reason } = useIsMergeAvailable({ mergeRequest, status, hasConflicts: false });
	const { merge, isMerging } = useMerge();

	const button = (
		<Button className="w-full" disabled={disabled || isMerging} size="md" variant="primary">
			{isMerging && <Loader size="sm" />}
			{t("git.merge.branches")}
		</Button>
	);

	if (disabled) return <Tooltip content={reason}>{button}</Tooltip>;

	return (
		<ConfirmPopover
			confirmText={t("git.merge.merge")}
			description={<MergeConfirmDescription mergeRequest={mergeRequest} />}
			icon="alert-circle"
			onConfirm={merge}
			status="warning"
			title={t("git.merge-requests.confirm.title")}
		>
			{button}
		</ConfirmPopover>
	);
};

export default MergeButton;
